import { Plus } from "lucide-react";
import * as React from "react";

import { Button } from "@/shared/foundation/primitives/Button";
import { usePopupState } from "@/shared/hooks/usePopupState";

import styles from "./DataTableActiveFilters.module.css";
import { type FilterDefinition } from "./DataTableFilters/FilterDefinition";
import { FilterMenu } from "./DataTableFilters/FilterMenu";
import { type Filter } from "./DataTableFilters/FilterSchema";
import { FilterChip } from "./FilterChip";
import { MatchModePicker } from "./MatchModePicker";
import { type DataTableMatchMode } from "./useDataTableState";

export interface DataTableActiveFiltersProps {
  /**
   * Currently-applied filters
   */
  filters: Filter[];
  /**
   * Filterable fields available in the picker. Used to resolve each
   * `filter.field` (wire-level path) to its human label for chip rendering,
   * and to populate the inline `+` add-filter menu.
   */
  filterDefinitions: FilterDefinition[];
  /**
   * Called with the index of the chip the user clicked `×` on.
   */
  onRemove: (index: number) => void;
  /**
   * Called when the user clicks the right-aligned Clear button. Should
   * remove every applied filter.
   */
  onClear: () => void;
  /**
   * How distinct filters join in the wire condition (AND / OR).
   */
  matchMode: DataTableMatchMode;
  /**
   * Called when the user picks a different option from the Match picker
   * menu.
   */
  onMatchModeChange: (next: DataTableMatchMode) => void;
  /**
   * Called when the user commits a filter from the inline `+` button's
   * menu. Receives the same `Filter` shape as `<DataTable.Filters>`'s
   * `onFilterSelected` — both triggers route through this handler.
   */
  onFilterSelected: (filter: Filter) => void;
}

/**
 * Chip row that visualizes the currently-applied filters from
 * `<DataTable.Filters>`. Renders nothing when `filters` is empty.
 */
export function DataTableActiveFilters({
  filters,
  filterDefinitions,
  onRemove,
  onClear,
  matchMode,
  onMatchModeChange,
  onFilterSelected,
}: DataTableActiveFiltersProps) {
  const addPopup = usePopupState();

  const handleAddClick = (event: React.MouseEvent<HTMLElement>) => {
    if (addPopup.isOpen) {
      addPopup.close();
    } else {
      addPopup.open({ anchorEl: event.currentTarget });
    }
  };

  if (filters.length === 0) {
    return null;
  }

  return (
    <div className={styles.bar} role="region" aria-label="Active filters">
      {filters.length >= 2 && (
        <MatchModePicker mode={matchMode} onChange={onMatchModeChange} />
      )}
      {filters.map((filter, i) => (
        <FilterChip
          key={`${filter.field}-${i}`}
          filter={filter}
          fieldName={resolveFieldName(filter, filterDefinitions)}
          onRemove={() => onRemove(i)}
        />
      ))}
      <button
        type="button"
        className={styles.add}
        onClick={handleAddClick}
        aria-haspopup="dialog"
        aria-expanded={addPopup.isOpen}
        aria-label="Add filter"
      >
        <Plus />
      </button>
      <FilterMenu
        open={addPopup.isOpen}
        anchorEl={addPopup.anchor}
        onClose={addPopup.close}
        filterDefinitions={filterDefinitions}
        onCommit={onFilterSelected}
      />
      <div className={styles.trailing}>
        <Button variant="text" size="small" color="inherit" onClick={onClear}>
          Clear
        </Button>
      </div>
    </div>
  );
}

// Hydration with a definition list that doesn't include `filter.field`
// (e.g. URL state restoring an unknown field) still renders something
// informative rather than an empty segment.
function resolveFieldName(filter: Filter, defs: FilterDefinition[]): string {
  return (
    defs.find((d) => d.field === filter.field && d.type === filter.type)
      ?.name ?? filter.field
  );
}
